import { CalendarIcon, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
  type KeyboardEvent,
  type ReactElement,
} from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/lib/utils';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const DAY_HEADERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface CalendarCell {
  day: number;
  offset: -1 | 0 | 1;
  date: Date;
}

function buildGrid(year: number, month: number): CalendarCell[] {
  const firstWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrev = new Date(year, month, 0).getDate();
  const cells: CalendarCell[] = [];

  for (let i = firstWeekday - 1; i >= 0; i--) {
    const day = daysInPrev - i;
    cells.push({ day, offset: -1, date: new Date(year, month - 1, day) });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, offset: 0, date: new Date(year, month, d) });
  }
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++) {
    cells.push({ day: d, offset: 1, date: new Date(year, month + 1, d) });
  }
  return cells;
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

function toIso(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function parseIso(v: string): Date | null {
  if (!v) return null;
  const d = new Date(`${v}T00:00:00`);
  return isNaN(d.getTime()) ? null : d;
}

/** Split a partial ISO date string (YYYY, YYYY-MM, or YYYY-MM-DD) into segments. */
function parsePartial(v: string): { year: string; month: string; day: string } {
  if (!v) return { year: '', month: '', day: '' };
  const [year = '', month = '', day = ''] = v.split('-');
  return { year, month, day };
}

/**
 * Assemble segments into a partial ISO string.
 * Only includes a segment if all preceding segments are complete (2 digits for month/day,
 * 4 digits for year), so partial entry never produces a malformed value.
 */
function assemblePartial(year: string, month: string, day: string): string {
  if (year.length !== 4) return '';
  if (month.length !== 2) return year;
  if (day.length !== 2) return `${year}-${month}`;
  return `${year}-${month}-${day}`;
}

function clampSegment(s: string, min: number, max: number): string {
  if (s.length < 2) return s;
  const n = parseInt(s, 10);
  return String(Math.max(min, Math.min(max, n))).padStart(2, '0');
}

export interface DateInputProps {
  readonly id?: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  /** Fires on every keystroke in any segment, even when the date is incomplete. */
  readonly onInputChange?: () => void;
  /** Fires whenever any raw segment changes — lets the parent detect orphaned month/day. */
  readonly onSegmentChange?: (segs: { year: string; month: string; day: string }) => void;
  readonly className?: string | undefined;
  readonly disabled?: boolean;
  readonly 'aria-required'?: boolean | 'true' | 'false';
  readonly 'aria-invalid'?: boolean | 'true' | 'false';
  readonly 'aria-describedby'?: string | undefined;
}

export function DateInput({
  id,
  value,
  onChange,
  onInputChange,
  onSegmentChange,
  className,
  disabled = false,
  ...ariaProps
}: DateInputProps): ReactElement {
  const today = new Date();

  const [year, setYear] = useState(() => parsePartial(value).year);
  const [month, setMonth] = useState(() => parsePartial(value).month);
  const [day, setDay] = useState(() => parsePartial(value).day);

  const [open, setOpen] = useState(false);
  // The calendar renders in a portal on document.body with fixed positioning, so
  // it escapes the dialog's `overflow-y-auto` clipping and never spills past the
  // modal edge. `popoverStyle` holds the computed viewport coordinates.
  const [popoverStyle, setPopoverStyle] = useState<CSSProperties | null>(null);
  const [showYearPicker, setShowYearPicker] = useState(false);
  const [viewYear, setViewYear] = useState(() => {
    const { year: y } = parsePartial(value);
    return y.length === 4 ? parseInt(y, 10) : today.getFullYear();
  });
  const [viewMonth, setViewMonth] = useState(() => {
    const { year: y, month: m } = parsePartial(value);
    return y.length === 4 && m.length === 2 ? parseInt(m, 10) - 1 : today.getMonth();
  });
  const [yearPageStart, setYearPageStart] = useState(() => {
    const { year: y } = parsePartial(value);
    const yr = y.length === 4 ? parseInt(y, 10) : today.getFullYear();
    return Math.floor(yr / 12) * 12;
  });

  const yearRef = useRef<HTMLInputElement>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const dayRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  // Tracks last value we emitted so the sync effect can skip echoes
  const lastEmittedRef = useRef(value);
  // Mirrors month/day state synchronously so blur handlers don't read stale closures
  // (auto-advance calls focus() inside onChange before React commits the new state)
  const latestMonthRef = useRef(month);
  const latestDayRef = useRef(day);

  // Sync segments when value is changed externally (parent reset, Clear button, etc.)
  useEffect(() => {
    if (value === lastEmittedRef.current) return;
    const parsed = parsePartial(value);
    setYear(parsed.year);
    setMonth(parsed.month);
    setDay(parsed.day);
    latestMonthRef.current = parsed.month;
    latestDayRef.current = parsed.day;
    lastEmittedRef.current = value;
  }, [value]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      // The popover is portaled outside `containerRef`, so check it too — otherwise
      // clicking inside the calendar would read as an outside click and close it.
      const insideTrigger = containerRef.current?.contains(target) ?? false;
      const insidePopover = popoverRef.current?.contains(target) ?? false;
      if (!insideTrigger && !insidePopover) {
        setOpen(false);
        setShowYearPicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => { document.removeEventListener('mousedown', handler); };
  }, []);

  // Position the portaled popover under (or above) the trigger, clamped to the
  // viewport. Recomputed on scroll (capture, to catch scrolling dialog bodies)
  // and resize while open.
  useLayoutEffect(() => {
    if (!open) return;
    const compute = (): void => {
      const el = containerRef.current;
      if (el === null) return;
      const rect = el.getBoundingClientRect();
      const POPOVER_W = 288; // w-72
      const EST_H = 360; // calendar / year-picker worst case
      const margin = 8;
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropUp = spaceBelow < EST_H && rect.top > spaceBelow;
      const left = Math.max(
        margin,
        Math.min(rect.left, window.innerWidth - POPOVER_W - margin),
      );
      setPopoverStyle({
        position: 'fixed',
        left,
        ...(dropUp
          ? { bottom: window.innerHeight - rect.top + 4 }
          : { top: rect.bottom + 4 }),
      });
    };
    compute();
    window.addEventListener('scroll', compute, true);
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('scroll', compute, true);
      window.removeEventListener('resize', compute);
    };
  }, [open]);

  const emit = (y: string, m: string, d: string) => {
    const assembled = assemblePartial(y, m, d);
    lastEmittedRef.current = assembled;
    onChange(assembled);
    onSegmentChange?.({ year: y, month: m, day: d });
  };

  const handleYearChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 4);
    setYear(raw);
    onInputChange?.();
    emit(raw, month, day);
  };

  const handleMonthChange = (e: ChangeEvent<HTMLInputElement>) => {
    let raw = e.target.value.replace(/\D/g, '').slice(0, 2);
    // First digit 2–9 can only form 02–09 — auto-pad
    if (raw.length === 1 && parseInt(raw, 10) >= 2) {
      raw = raw.padStart(2, '0');
    } else {
      raw = clampSegment(raw, 1, 12);
    }
    setMonth(raw);
    latestMonthRef.current = raw;
    onInputChange?.();
    emit(year, raw, day);
    if (raw.length === 2) dayRef.current?.focus();
  };

  const handleDayChange = (e: ChangeEvent<HTMLInputElement>) => {
    let raw = e.target.value.replace(/\D/g, '').slice(0, 2);
    // First digit 4–9 can only form 04–09 — auto-pad immediately
    if (raw.length === 1 && parseInt(raw, 10) >= 4) {
      raw = raw.padStart(2, '0');
    } else {
      raw = clampSegment(raw, 1, 31);
    }
    setDay(raw);
    latestDayRef.current = raw;
    onInputChange?.();
    emit(year, month, raw);
    if (raw.length === 2) yearRef.current?.focus();
  };

  // Pad a trailing single digit on blur. Reads from refs (not state) to avoid the stale
  // closure bug where auto-advance fires focus() inside onChange before React commits state.
  const handleMonthBlur = () => {
    const cur = latestMonthRef.current;
    if (cur.length === 1) {
      const padded = clampSegment(cur.padStart(2, '0'), 1, 12);
      setMonth(padded);
      latestMonthRef.current = padded;
      emit(year, padded, latestDayRef.current);
    }
  };

  const handleDayBlur = () => {
    const cur = latestDayRef.current;
    if (cur.length === 1) {
      const padded = clampSegment(cur.padStart(2, '0'), 1, 31);
      setDay(padded);
      latestDayRef.current = padded;
      emit(year, latestMonthRef.current, padded);
    }
  };

  // Fill order matches display order MM / DD / YYYY: tabbing/clicking in lands on
  // month first, auto-advancing month → day → year as each segment fills.
  const handleYearKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && year === '') {
      e.preventDefault();
      dayRef.current?.focus();
    }
    if (e.key === 'Escape') setOpen(false);
  };

  const handleMonthKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') setOpen(false);
  };

  const handleDayKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && day === '') {
      e.preventDefault();
      monthRef.current?.focus();
    }
    if (e.key === 'Escape') setOpen(false);
  };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1); }
    else setViewMonth((m) => m - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1); }
    else setViewMonth((m) => m + 1);
  };

  const openCalendar = () => {
    if (disabled) return;
    if (year.length === 4 && month.length === 2) {
      const y = parseInt(year, 10);
      const m = parseInt(month, 10) - 1;
      setViewYear(y);
      setViewMonth(Math.max(0, Math.min(11, m)));
      setYearPageStart(Math.floor(y / 12) * 12);
    }
    setShowYearPicker(false);
    setOpen((prev) => !prev);
  };

  const handleDayClick = (cell: CalendarCell) => {
    const iso = toIso(cell.date);
    const y = String(cell.date.getFullYear());
    const m = String(cell.date.getMonth() + 1).padStart(2, '0');
    const d = String(cell.date.getDate()).padStart(2, '0');
    setYear(y);
    setMonth(m);
    setDay(d);
    latestMonthRef.current = m;
    latestDayRef.current = d;
    lastEmittedRef.current = iso;
    onChange(iso);
    onSegmentChange?.({ year: y, month: m, day: d });
    if (cell.offset === -1) prevMonth();
    else if (cell.offset === 1) nextMonth();
    setOpen(false);
  };

  const selected: Date | null = (year.length === 4 && month.length === 2 && day.length === 2)
    ? parseIso(`${year}-${month}-${day}`)
    : null;

  const cells = buildGrid(viewYear, viewMonth);
  const yearPage = Array.from({ length: 12 }, (_, i) => yearPageStart + i);
  const isInvalid = ariaProps['aria-invalid'] === true || ariaProps['aria-invalid'] === 'true';

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* Segment inputs + calendar icon */}
      <div className={cn(
        'flex h-10 w-full items-center rounded-md border border-input bg-card shadow-xs',
        disabled && 'cursor-not-allowed opacity-50',
        isInvalid && 'border-destructive',
      )}>
        <div className="flex flex-1 items-center gap-0.5 px-3">
          <input
            ref={monthRef}
            id={id}
            type="text"
            inputMode="numeric"
            value={month}
            onChange={handleMonthChange}
            onKeyDown={handleMonthKeyDown}
            onBlur={handleMonthBlur}
            disabled={disabled}
            placeholder="MM"
            maxLength={2}
            className="w-8 bg-transparent text-center text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"
            {...ariaProps}
          />
          <span className="select-none text-muted-foreground">/</span>
          <input
            ref={dayRef}
            type="text"
            inputMode="numeric"
            value={day}
            onChange={handleDayChange}
            onKeyDown={handleDayKeyDown}
            onBlur={handleDayBlur}
            disabled={disabled}
            placeholder="DD"
            maxLength={2}
            className="w-8 bg-transparent text-center text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"
          />
          <span className="select-none text-muted-foreground">/</span>
          <input
            ref={yearRef}
            type="text"
            inputMode="numeric"
            value={year}
            onChange={handleYearChange}
            onKeyDown={handleYearKeyDown}
            disabled={disabled}
            placeholder="YYYY"
            maxLength={4}
            className="w-12 bg-transparent text-center text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"
          />
        </div>
        <button
          type="button"
          tabIndex={-1}
          disabled={disabled}
          onClick={openCalendar}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label="Open calendar"
          className="flex h-full w-9 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground disabled:pointer-events-none"
        >
          <CalendarIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      {/* Popover — portaled to <body> with fixed positioning so it escapes the
          dialog's scroll/overflow clipping and stays within the viewport. */}
      {open && popoverStyle !== null && createPortal(
        // Stop pointer/mouse events from reaching Radix's DismissableLayer so
        // interacting with the calendar doesn't dismiss the surrounding dialog.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- guard clicks, not user-facing interaction
        <div
          ref={popoverRef}
          role="dialog"
          aria-label="Date picker"
          style={popoverStyle}
          onPointerDown={(e) => { e.stopPropagation(); }}
          onMouseDown={(e) => { e.stopPropagation(); }}
          // `pointer-events-auto`: a modal Radix dialog sets `pointer-events: none`
          // on <body>; the portaled popover is a body child, so it must re-enable
          // its own pointer events or the day buttons can't be clicked.
          className="pointer-events-auto z-[60] w-72 rounded-lg border border-border bg-popover text-popover-foreground shadow-lg"
        >
          {showYearPicker ? (
            /* ── Year + Month picker ───────────────────────── */
            <div className="p-3 space-y-3">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <button
                    type="button"
                    onClick={() => { setYearPageStart((s) => s - 12); }}
                    className="rounded p-1 hover:bg-accent hover:text-accent-foreground"
                    aria-label="Previous years"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    {yearPage[0]}–{yearPage[11]}
                  </span>
                  <button
                    type="button"
                    onClick={() => { setYearPageStart((s) => s + 12); }}
                    className="rounded p-1 hover:bg-accent hover:text-accent-foreground"
                    aria-label="Next years"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
                <div className="grid grid-cols-4 gap-1">
                  {yearPage.map((yr) => (
                    <button
                      key={yr}
                      type="button"
                      onClick={() => { setViewYear(yr); }}
                      className={cn(
                        'rounded py-1 text-xs hover:bg-accent hover:text-accent-foreground',
                        yr === viewYear && 'bg-primary text-primary-foreground font-bold hover:bg-primary/90 hover:text-primary-foreground',
                      )}
                    >
                      {yr}
                    </button>
                  ))}
                </div>
              </div>

              <div className="border-t border-border" />

              <div>
                <p className="mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Month</p>
                <div className="grid grid-cols-4 gap-1">
                  {MONTH_NAMES.map((name, idx) => (
                    <button
                      key={name}
                      type="button"
                      onClick={() => { setViewMonth(idx); setShowYearPicker(false); }}
                      className={cn(
                        'rounded py-1 text-xs hover:bg-accent hover:text-accent-foreground',
                        idx === viewMonth && 'bg-primary text-primary-foreground font-bold hover:bg-primary/90 hover:text-primary-foreground',
                      )}
                    >
                      {name.slice(0, 3)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            /* ── Month/day picker ──────────────────────────── */
            <div className="p-3">
              <div className="flex items-center justify-between mb-2">
                <button
                  type="button"
                  onClick={prevMonth}
                  className="rounded p-1 hover:bg-accent hover:text-accent-foreground"
                  aria-label="Previous month"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => { setShowYearPicker(true); }}
                  className="flex items-center gap-1 rounded px-2 py-1 text-sm font-semibold hover:bg-accent hover:text-accent-foreground"
                >
                  {MONTH_NAMES[viewMonth]} {viewYear}
                  <ChevronDown className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  onClick={nextMonth}
                  className="rounded p-1 hover:bg-accent hover:text-accent-foreground"
                  aria-label="Next month"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>

              <div className="grid grid-cols-7 mb-1">
                {DAY_HEADERS.map((h) => (
                  <div key={h} className="py-1 text-center text-xs font-medium text-muted-foreground">
                    {h}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7">
                {cells.map((cell, i) => {
                  const sel = selected !== null && isSameDay(cell.date, selected);
                  const tod = isSameDay(cell.date, today);
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => { handleDayClick(cell); }}
                      className={cn(
                        'flex h-8 w-full items-center justify-center rounded-full text-sm',
                        cell.offset !== 0 && 'text-muted-foreground/50',
                        cell.offset === 0 && !sel && 'hover:bg-accent hover:text-accent-foreground',
                        sel && 'bg-primary text-primary-foreground font-semibold',
                        !sel && tod && cell.offset === 0 && 'text-primary font-semibold',
                      )}
                    >
                      {cell.day}
                    </button>
                  );
                })}
              </div>

              <div className="mt-2 border-t border-border pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setYear(''); setMonth(''); setDay('');
                    latestMonthRef.current = '';
                    latestDayRef.current = '';
                    lastEmittedRef.current = '';
                    onChange('');
                    onSegmentChange?.({ year: '', month: '', day: '' });
                    setOpen(false);
                  }}
                  className="rounded border border-border px-3 py-1 text-xs text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                >
                  Clear
                </button>
              </div>
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
